import { motion } from "framer-motion";
import {
    Eye,
    Info,
    Loader2,
    MessageCircle,
    MessageSquare,
    Pencil,
    Plus,
    Save,
    Settings,
    Sparkles,
    Trash2,
    X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import ConfirmModal from "../components/common/ConfirmModal";
import { api } from "../lib/api";


const CATEGORY_LABELS = {
    no_answer: "Pas de réponse",
    recap_interview: "Récap entretien",
    immersion_reminder: "Rappel immersion",
    onboarding: "Onboarding",
    rejection: "Refus",
    custom: "Personnalisé",
};


export default function MessageTemplatesAdmin() {
    const [templates, setTemplates] = useState([]);
    const [variables, setVariables] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);          // template being edited (modal)
    const [creating, setCreating] = useState(false);
    const [deleting, setDeleting] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [{ data: tpls }, { data: vars }] = await Promise.all([
                api.get("/message-templates"),
                api.get("/message-templates/variables"),
            ]);
            setTemplates(tpls);
            setVariables(vars);
        } catch {
            toast.error("Erreur de chargement");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    async function handleSave(template) {
        try {
            if (template.id) {
                await api.patch(`/message-templates/${template.id}`, template);
                toast.success("Modèle mis à jour");
            } else {
                await api.post("/message-templates", template);
                toast.success("Modèle créé");
            }
            setEditing(null);
            setCreating(false);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        }
    }

    async function handleDelete(template) {
        try {
            await api.delete(`/message-templates/${template.id}`);
            toast.success("Modèle supprimé");
            setDeleting(null);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        }
    }

    if (loading) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-shariti-orange" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="templates-admin-page">
            <header className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-shariti-orange-soft text-shariti-orange px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                        <Sparkles className="h-3 w-3" /> Back-office · Messages
                    </div>
                    <h1 className="mt-3 text-3xl md:text-4xl font-black tracking-tight text-shariti-black">
                        Modèles de communication
                    </h1>
                    <p className="mt-2 text-sm font-medium text-slate-500 max-w-2xl">
                        Personnalisez les SMS / WhatsApp envoyés aux candidats. Les variables comme <code className="px-1 rounded bg-shariti-cream text-shariti-orange text-[11px]">{"{{prenom}}"}</code> sont automatiquement remplacées.
                    </p>
                </div>
                <button
                    onClick={() => setCreating(true)}
                    data-testid="template-create"
                    className="btn-shariti"
                >
                    <Plus className="h-4 w-4" />
                    Nouveau modèle
                </button>
            </header>

            <div className="grid lg:grid-cols-[1fr,320px] gap-6">
                <div className="space-y-4">
                    {templates.map((t) => (
                        <motion.div
                            key={t.id}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="card-soft p-5"
                            data-testid={`template-card-${t.id}`}
                        >
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className="text-base font-extrabold text-shariti-black">{t.name}</h3>
                                        {t.is_default && (
                                            <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-shariti-orange text-white">DÉFAUT</span>
                                        )}
                                        {t.is_built_in && (
                                            <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-shariti-cream text-slate-500 border border-shariti-gray">INTÉGRÉ</span>
                                        )}
                                        <span className="text-[10px] font-bold uppercase tracking-widest text-shariti-orange">
                                            {CATEGORY_LABELS[t.category] || t.category}
                                        </span>
                                    </div>
                                    {t.description && (
                                        <p className="text-xs font-medium text-slate-500 mt-1">{t.description}</p>
                                    )}
                                    <div className="mt-3 grid md:grid-cols-2 gap-3">
                                        <Preview icon={MessageSquare} label="SMS" body={t.body_sms} color="text-shariti-orange" />
                                        <Preview icon={MessageCircle} label="WhatsApp" body={t.body_whatsapp} color="text-emerald-600" />
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <button
                                        onClick={() => setEditing(t)}
                                        data-testid={`template-edit-${t.id}`}
                                        className="inline-flex items-center gap-1.5 rounded-full bg-white border border-shariti-gray text-slate-600 hover:text-shariti-orange hover:border-shariti-orange px-3 h-8 font-bold text-[11px] transition"
                                    >
                                        <Pencil className="h-3 w-3" /> Modifier
                                    </button>
                                    {!t.is_built_in && (
                                        <button
                                            onClick={() => setDeleting(t)}
                                            data-testid={`template-delete-${t.id}`}
                                            className="inline-flex items-center justify-center gap-1.5 rounded-full bg-white border border-red-200 text-shariti-red hover:bg-red-50 px-3 h-8 font-bold text-[11px] transition"
                                            title="Supprimer ce modèle"
                                        >
                                            <Trash2 className="h-3 w-3" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Variables sidebar */}
                <aside className="card-soft p-5 h-fit sticky top-4" data-testid="template-variables-panel">
                    <div className="flex items-center gap-2 mb-2">
                        <Info className="h-4 w-4 text-shariti-orange" />
                        <h3 className="text-sm font-extrabold text-shariti-black">Variables disponibles</h3>
                    </div>
                    <p className="text-[11px] font-medium text-slate-500 mb-3">
                        Cliquez pour copier. Insérez-les dans vos modèles.
                    </p>
                    <ul className="space-y-1 max-h-[60vh] overflow-y-auto">
                        {variables.map((v) => (
                            <li key={v.key}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        navigator.clipboard?.writeText(`{{${v.key}}}`).then(() => {
                                            toast.success(`{{${v.key}}} copié`);
                                        });
                                    }}
                                    className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-shariti-orange-soft transition group"
                                >
                                    <code className="text-[11px] font-bold text-shariti-orange">{"{{" + v.key + "}}"}</code>
                                    <span className="text-[10px] font-medium text-slate-500 ml-2">{v.label}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                    <p className="text-[10px] font-medium text-slate-400 mt-3 italic">
                        Astuce : modifiez les paramètres globaux (salaire, signature...) depuis{" "}
                        <Link
                            to="/admin/parametres-entreprise"
                            className="text-shariti-orange font-extrabold hover:underline underline-offset-2 not-italic"
                            data-testid="link-tenant-settings"
                        >
                            Paramètres entreprise →
                        </Link>
                    </p>
                </aside>
            </div>

            {(editing || creating) && (
                <TemplateEditor
                    template={editing}
                    onCancel={() => { setEditing(null); setCreating(false); }}
                    onSave={handleSave}
                />
            )}

            {deleting && (
                <ConfirmModal
                    title="Supprimer ce modèle ?"
                    body={<>Le modèle <strong>{deleting.name}</strong> sera supprimé définitivement.</>}
                    confirmLabel="Supprimer"
                    tone="red"
                    onCancel={() => setDeleting(null)}
                    onConfirm={() => handleDelete(deleting)}
                />
            )}
        </div>
    );
}


function Preview({ icon: Icon, label, body, color }) {
    return (
        <div className="rounded-xl bg-shariti-cream/40 border border-shariti-gray p-3">
            <div className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest ${color} mb-1.5`}>
                <Icon className="h-3 w-3" /> {label}
            </div>
            {body ? (
                <pre className="text-[10px] font-mono text-slate-700 whitespace-pre-wrap leading-snug line-clamp-5">
                    {body}
                </pre>
            ) : (
                <div className="text-[10px] font-medium text-slate-400 italic">Aucun contenu — utilise la version {label === "SMS" ? "WhatsApp" : "SMS"}.</div>
            )}
        </div>
    );
}


function TemplateEditor({ template, onCancel, onSave }) {
    const [form, setForm] = useState(() => ({
        id: template?.id || null,
        name: template?.name || "",
        description: template?.description || "",
        category: template?.category || "custom",
        body_sms: template?.body_sms || "",
        body_whatsapp: template?.body_whatsapp || "",
        is_default: template?.is_default || false,
    }));
    const [previewMode, setPreviewMode] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const isBuiltIn = !!template?.is_built_in;
    const isNew = !template?.id;

    useEffect(() => {
        function onKey(e) { if (e.key === "Escape") onCancel(); }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onCancel]);

    async function loadPreview() {
        try {
            // Find any candidate to test with (admin-only call)
            const { data: candidates } = await api.get("/candidates?limit=1");
            if (!candidates?.length) {
                toast.error("Aucun candidat pour prévisualiser");
                return;
            }
            const cand = candidates[0];
            const { data } = await api.post(`/candidates/${cand.id}/messages/preview`, {
                custom_body_sms: form.body_sms,
                custom_body_whatsapp: form.body_whatsapp,
            });
            setPreviewData({ ...data, candidate: cand });
        } catch {
            toast.error("Erreur lors de l'aperçu");
        }
    }

    function submit(e) {
        e.preventDefault();
        if (!form.name.trim()) { toast.error("Nom requis"); return; }
        if (!form.body_sms?.trim() && !form.body_whatsapp?.trim()) {
            toast.error("Renseignez au moins une version (SMS ou WhatsApp)");
            return;
        }
        onSave(form);
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-50 bg-shariti-black/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
            data-testid="template-editor-modal"
            onClick={onCancel}
        >
            <motion.form
                onSubmit={submit}
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-3xl shadow-2xl p-6 md:p-8 max-w-4xl w-full max-h-[92vh] flex flex-col my-8"
            >
                <div className="flex items-start justify-between gap-4 mb-5 shrink-0">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full bg-shariti-orange-soft text-shariti-orange px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                            <Settings className="h-3 w-3" /> {isNew ? "Nouveau modèle" : "Modifier"}
                        </div>
                        <h2 className="mt-3 text-xl font-black tracking-tight text-shariti-black">
                            {form.name || "Sans nom"}
                        </h2>
                    </div>
                    <button type="button" onClick={onCancel} className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-shariti-cream text-slate-500 hover:text-shariti-red hover:bg-red-50 transition shrink-0">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="overflow-y-auto flex-1 -mx-2 px-2 space-y-4">
                    <div className="grid md:grid-cols-2 gap-3">
                        <label className="block">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 block">Nom du modèle</span>
                            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-shariti" data-testid="template-form-name" placeholder="ex. Récap entretien" />
                        </label>
                        <label className="block">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 block">Catégorie</span>
                            <select
                                value={form.category}
                                onChange={(e) => setForm({ ...form, category: e.target.value })}
                                data-testid="template-form-category"
                                disabled={isBuiltIn}
                                className="input-shariti disabled:opacity-60"
                            >
                                {Object.entries(CATEGORY_LABELS).map(([k, l]) => (
                                    <option key={k} value={k}>{l}</option>
                                ))}
                            </select>
                        </label>
                    </div>
                    <label className="block">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 block">Description (interne)</span>
                        <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-shariti" placeholder="Quand et pourquoi utiliser ce modèle" />
                    </label>

                    <label className="flex items-center gap-2 text-xs font-bold text-shariti-black">
                        <input
                            type="checkbox"
                            checked={form.is_default}
                            onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
                            data-testid="template-form-default"
                            className="h-4 w-4 accent-shariti-orange"
                        />
                        Définir comme modèle par défaut pour cette catégorie
                    </label>

                    <div className="grid md:grid-cols-2 gap-4">
                        <label className="block">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-shariti-orange mb-1.5 flex items-center gap-1.5">
                                <MessageSquare className="h-3 w-3" /> Version SMS (courte)
                            </span>
                            <textarea
                                value={form.body_sms}
                                onChange={(e) => setForm({ ...form, body_sms: e.target.value })}
                                data-testid="template-form-body-sms"
                                rows={10}
                                className="input-shariti font-mono text-[12px] leading-relaxed resize-y"
                                placeholder="Bonjour {{prenom}}..."
                            />
                            <p className="mt-1 text-[10px] font-medium text-slate-400">{form.body_sms.length} caractères{form.body_sms.length > 160 && ` · ${Math.ceil(form.body_sms.length / 160)} SMS`}</p>
                        </label>
                        <label className="block">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-600 mb-1.5 flex items-center gap-1.5">
                                <MessageCircle className="h-3 w-3" /> Version WhatsApp (longue)
                            </span>
                            <textarea
                                value={form.body_whatsapp}
                                onChange={(e) => setForm({ ...form, body_whatsapp: e.target.value })}
                                data-testid="template-form-body-whatsapp"
                                rows={10}
                                className="input-shariti font-mono text-[12px] leading-relaxed resize-y"
                                placeholder="Bonjour {{prenom}} 👋..."
                            />
                            <p className="mt-1 text-[10px] font-medium text-slate-400">{form.body_whatsapp.length} caractères · {(form.body_whatsapp.match(/\n/g) || []).length + 1} lignes</p>
                        </label>
                    </div>

                    <button
                        type="button"
                        onClick={() => { loadPreview(); setPreviewMode(true); }}
                        data-testid="template-form-preview"
                        className="text-[11px] font-extrabold uppercase tracking-widest text-shariti-orange hover:text-shariti-black transition inline-flex items-center gap-1.5"
                    >
                        <Eye className="h-3.5 w-3.5" />
                        Aperçu avec un vrai candidat
                    </button>

                    {previewMode && previewData && (
                        <div className="rounded-2xl bg-shariti-cream/40 border border-shariti-gray p-4 space-y-3">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                                Aperçu pour : {previewData.candidate?.first_name} {previewData.candidate?.last_name}
                            </div>
                            <pre className="text-[11px] font-mono text-slate-700 whitespace-pre-wrap leading-relaxed bg-white p-3 rounded-xl border border-shariti-gray">
                                <strong className="text-shariti-orange">— SMS —</strong>
                                {"\n"}{previewData.rendered_sms}
                            </pre>
                            <pre className="text-[11px] font-mono text-slate-700 whitespace-pre-wrap leading-relaxed bg-white p-3 rounded-xl border border-shariti-gray">
                                <strong className="text-emerald-600">— WhatsApp —</strong>
                                {"\n"}{previewData.rendered_whatsapp}
                            </pre>
                        </div>
                    )}
                </div>

                <div className="mt-5 flex items-center justify-end gap-3 shrink-0 border-t border-shariti-gray pt-4">
                    <button type="button" onClick={onCancel} className="inline-flex items-center gap-2 rounded-full bg-white border border-shariti-gray text-slate-600 hover:text-shariti-red hover:border-shariti-red px-5 h-10 font-bold text-xs transition">
                        Annuler
                    </button>
                    <button type="submit" data-testid="template-form-submit" className="btn-shariti">
                        <Save className="h-4 w-4" />
                        {isNew ? "Créer le modèle" : "Enregistrer"}
                    </button>
                </div>
            </motion.form>
        </motion.div>
    );
}
